/**
 * RSVP 표준 컨트롤러.
 *
 * 디자이너가 심어둔 form([data-rsvp-form])과 모달([data-rsvp-overlay])을 탐색해:
 *  - 섹션 내부의 .btn-primary / [data-rsvp-open] 클릭 시 모달 오픈
 *  - overlay 배경 또는 [data-rsvp-close] / #rsvpClose / .rsvp-close 클릭 시 닫기
 *  - .rsvp-privacy 안의 a (또는 [data-rsvp-privacy-detail]) 클릭 시
 *    개인정보 수집·이용 동의 detail 모달 오픈 (PIPA 제15조 고지 사항)
 *  - form submit 시 POST /api/invitations/{invitationId}/rsvp
 *    (#privacyCheck 미체크면 차단, 체크되어 있으면 privacyConsentedAt + version 전송)
 *  - preview/disabled 모드 처리
 *
 * Liquid 변수 {{ invitation.id }} 와 {{ viewerMode }} 를 참조하므로 서버 렌더러가 치환.
 *
 * 참고: web 뷰어에는 lib/viewerScript.ts(A)의 RSVP 핸들러도 함께 주입되지만,
 * 서버 /public 이 이 컨트롤러를 htmlContent 에 baking 해 DOM상 먼저 등록되므로
 * stopImmediatePropagation 으로 이 컨트롤러(B)가 이긴다. A의 RSVP는 폴백.
 */
export const RSVP_CONTROLLER_SCRIPT = `
<script data-fw-rsvp-controller>
(function () {
  var INVITATION_ID = {{ invitation.id | default: '' | json }};
  var MODE = {{ viewerMode | default: 'live' | json }};
  var AUTO_POPUP = {{ settings.rsvpAutoPopup | default: false | json }};
  var API_BASE = INVITATION_ID ? '/api/invitations/' + INVITATION_ID : '';
  function pick(sel, root) { return (root || document).querySelector(sel); }
  function findForm() { return pick('[data-rsvp-form]') || pick('#rsvpForm'); }
  function findOverlay() { return pick('[data-rsvp-overlay]') || pick('#rsvpOverlay'); }
  function findSubmit(form) { return form.querySelector('[data-rsvp-submit]') || form.querySelector('.rsvp-submit'); }
  var toastTimer = null;
  function toast(msg) {
    var t = document.getElementById('__fwToast');
    if (!t) {
      t = document.createElement('div');
      t.id = '__fwToast';
      t.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.85);color:#fff;padding:12px 20px;border-radius:8px;font-size:14px;z-index:9999;opacity:0;transition:opacity .2s;pointer-events:none;';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.style.opacity = '1';
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.style.opacity = '0'; }, 1800);
  }
  function openModal() {
    var ov = findOverlay(); if (!ov) return;
    ov.classList.add('active');
    if (getComputedStyle(ov).display === 'none') ov.style.display = 'flex';
    if (window.__fwScrollLock) window.__fwScrollLock.lock({ allowScrollWithin: ov });
    else document.body.style.overflow = 'hidden';
  }
  function closeModal() {
    var ov = findOverlay(); if (!ov) return;
    ov.classList.remove('active');
    ov.style.display = 'none';
    if (window.__fwScrollLock) window.__fwScrollLock.unlock();
    else document.body.style.overflow = '';
  }
  function wireOpeners() {
    var section = pick('[data-section="rsvp"]');
    if (section) {
      var els = section.querySelectorAll('[data-rsvp-open], .btn-primary, button');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (el.dataset.fwRsvpOpenInit) continue;
        if (el.closest('[data-rsvp-form]') || el.closest('[data-rsvp-overlay]')) continue;
        el.dataset.fwRsvpOpenInit = '1';
        el.addEventListener('click', function (e) { e.preventDefault(); openModal(); });
      }
    }
    var ov = findOverlay();
    if (ov && !ov.dataset.fwRsvpOverlayInit) {
      ov.dataset.fwRsvpOverlayInit = '1';
      ov.addEventListener('click', function (e) { if (e.target === ov) closeModal(); });
    }
    var closeBtns = document.querySelectorAll('[data-rsvp-close], #rsvpClose, .rsvp-close');
    for (var j = 0; j < closeBtns.length; j++) {
      var cb = closeBtns[j];
      if (cb.dataset.fwRsvpCloseInit) continue;
      cb.dataset.fwRsvpCloseInit = '1';
      cb.addEventListener('click', closeModal);
    }
    // [자세히보기] (개인정보 수집·이용 동의 상세) — .rsvp-privacy 내부의 a 또는 명시적 셀렉터
    var detailLinks = document.querySelectorAll('[data-rsvp-privacy-detail], .rsvp-privacy a');
    for (var k = 0; k < detailLinks.length; k++) {
      var dl = detailLinks[k];
      if (dl.dataset.fwRsvpPrivacyDetailInit) continue;
      dl.dataset.fwRsvpPrivacyDetailInit = '1';
      dl.addEventListener('click', function (e) { e.preventDefault(); openPrivacyDetail(); });
    }
  }
  function el(tag, css, text) {
    var n = document.createElement(tag);
    if (css) n.style.cssText = css;
    if (text) n.textContent = text;
    return n;
  }
  function appendDt(dl, label) { dl.appendChild(el('dt', 'font-weight:600;margin-top:10px;', label)); }
  function appendDd(dl, text) { dl.appendChild(el('dd', 'margin:4px 0 0;', text)); }
  function openPrivacyDetail() {
    var existing = document.getElementById('__fwRsvpPrivacyDetail');
    if (existing) {
      existing.style.display = 'flex';
      // RSVP 모달 위에 겹쳐 열리는 중첩 모달 — depth 갱신(내부 box 는 overflow-y:auto)
      if (existing.dataset.fwLocked !== '1') {
        existing.dataset.fwLocked = '1';
        if (window.__fwScrollLock) window.__fwScrollLock.lock({ allowScrollWithin: existing.querySelector('div') || existing });
      }
      return;
    }
    var ov = el('div', 'position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;');
    ov.id = '__fwRsvpPrivacyDetail';
    var box = el('div', 'background:#fff;border-radius:12px;max-width:420px;width:100%;max-height:80vh;overflow-y:auto;padding:24px;box-shadow:0 8px 32px rgba(0,0,0,.2);font-family:inherit;color:#222;line-height:1.6;font-size:14px;');
    var head = el('div', 'display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;');
    head.appendChild(el('h3', 'margin:0;font-size:16px;font-weight:700;', '개인정보 수집·이용 동의'));
    var x = el('button', 'background:none;border:0;font-size:22px;line-height:1;cursor:pointer;color:#666;padding:0;width:28px;height:28px;', '×');
    x.type = 'button';
    x.setAttribute('data-fw-rsvp-privacy-close', '');
    head.appendChild(x);
    box.appendChild(head);
    box.appendChild(el('p', 'margin:0 0 12px;color:#555;', '「개인정보 보호법」 제15조에 따라 아래 사항을 안내드립니다.'));
    var dl = el('dl', 'margin:0 0 16px;');
    appendDt(dl, '1. 수집·이용 목적');
    appendDd(dl, '결혼식 참석 여부 확인 및 예식 운영');
    appendDt(dl, '2. 수집 항목');
    appendDd(dl, '성함, (선택) 동행인 성함, 참석여부, 식사여부, 전세버스 탑승여부, 전달사항');
    appendDt(dl, '3. 보유 및 이용 기간');
    appendDd(dl, '예식 종료 후 90일 또는 청첩장 만료 시까지');
    appendDt(dl, '4. 동의 거부권');
    appendDd(dl, '동의를 거부할 권리가 있으며, 거부 시 참석 여부 전달이 제한됩니다.');
    box.appendChild(dl);
    box.appendChild(el('p', 'margin:0 0 16px;font-size:12px;color:#888;', '약관 버전: 2026-05-08'));
    var ok = el('button', 'width:100%;padding:12px;background:#222;color:#fff;border:0;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;', '확인');
    ok.type = 'button';
    ok.setAttribute('data-fw-rsvp-privacy-close', '');
    box.appendChild(ok);
    ov.appendChild(box);
    function close() {
      ov.style.display = 'none';
      if (ov.dataset.fwLocked === '1') { ov.dataset.fwLocked = '0'; if (window.__fwScrollLock) window.__fwScrollLock.unlock(); }
    }
    ov.addEventListener('click', function (e) { if (e.target === ov) close(); });
    var btns = ov.querySelectorAll('[data-fw-rsvp-privacy-close]');
    for (var i = 0; i < btns.length; i++) btns[i].addEventListener('click', close);
    document.body.appendChild(ov);
    // 최초 생성·표시 → 중첩 락(box 내부 overflow-y:auto 스크롤은 허용)
    ov.dataset.fwLocked = '1';
    if (window.__fwScrollLock) window.__fwScrollLock.lock({ allowScrollWithin: box });
  }
  // 자동 팝업 억제 키 — 제출(영구/localStorage) · 세션 표시(1회/sessionStorage)
  var SUBMITTED_KEY = 'fw_rsvp_submitted_' + INVITATION_ID;
  var SHOWN_KEY = 'fw_rsvp_autopopup_' + INVITATION_ID;
  function markSubmitted() {
    try { localStorage.setItem(SUBMITTED_KEY, '1'); } catch (e) {}
  }
  function maybeAutoPopup() {
    // live 모드 + 설정 ON + 모달 존재 + 미제출 + 이번 세션 미표시일 때만 1회
    if (!(AUTO_POPUP && MODE === 'live' && findOverlay())) return;
    var submitted = false, shownThisSession = false;
    try { submitted = localStorage.getItem(SUBMITTED_KEY) === '1'; } catch (e) {}
    try { shownThisSession = sessionStorage.getItem(SHOWN_KEY) === '1'; } catch (e) {}
    if (submitted || shownThisSession) return;
    try { sessionStorage.setItem(SHOWN_KEY, '1'); } catch (e) {}
    setTimeout(function () { openModal(); }, 600); // 인트로/로딩 오버레이 페이드 후
  }
  function init() {
    var form = findForm();
    if (!form) return;
    wireOpeners();
    maybeAutoPopup();
    if (form.dataset.fwRsvpInit) return;
    form.dataset.fwRsvpInit = '1';
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      var btn = findSubmit(form);
      if (!btn || btn.disabled) return;
      if (MODE === 'preview') { toast('미리보기입니다. 청첩장을 생성하면 실제로 저장됩니다.'); form.reset(); closeModal(); return; }
      if (MODE === 'disabled' || !API_BASE) { toast('청첩장을 먼저 생성해주세요.'); return; }
      var fd = new FormData(form);
      var guestName = (fd.get('name') || '').toString().trim();
      if (!guestName) { toast('성함을 입력해주세요.'); return; }
      // 개인정보 수집·이용 동의 체크 검증 (PIPA 제15조)
      var privacyEl = form.querySelector('#privacyCheck, [data-rsvp-privacy-check]');
      if (privacyEl && !privacyEl.checked) { toast('개인정보 수집·이용에 동의해주세요.'); return; }
      var attendVal = (fd.get('attend') || '').toString();
      var attendance = attendVal === 'yes' ? 'ATTENDING' : attendVal === 'no' ? 'NOT_ATTENDING' : 'MAYBE';
      var companion = parseInt((fd.get('companionCount') || '0').toString(), 10);
      var guestCount = Math.max(1, Math.min(10, (isNaN(companion) ? 0 : companion) + 1));
      var mealVal = (fd.get('meal') || '').toString();
      var body = { guestName: guestName, attendance: attendance, guestCount: guestCount };
      if (mealVal === 'yes') body.mealOption = 'ADULT';
      else if (mealVal === 'no') body.mealOption = 'NONE';
      var parts = [];
      var side = (fd.get('side') || '').toString();
      if (side) parts.push(side === 'groom' ? '[신랑측]' : '[신부측]');
      var bus = (fd.get('bus') || '').toString();
      if (bus) parts.push('[버스: ' + (bus === 'yes' ? 'O' : 'X') + ']');
      var companionName = (fd.get('companionName') || '').toString().trim();
      if (companionName) parts.push('[동행인: ' + companionName + ']');
      var userMessage = (fd.get('message') || '').toString().trim();
      if (userMessage) parts.push(userMessage);
      if (parts.length) body.message = parts.join(' ');
      body.privacyConsentedAt = new Date().toISOString();
      body.privacyConsentVersion = '2026-05-08';
      var orig = btn.textContent;
      btn.disabled = true; btn.textContent = '전달 중...';
      fetch(API_BASE + '/rsvp', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
        .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, status: res.status, data: d }; }); })
        .then(function (r) {
          btn.disabled = false; btn.textContent = orig;
          if (r.ok && r.data.success) { markSubmitted(); toast('참석 여부가 전달되었습니다. 감사합니다!'); form.reset(); closeModal(); }
          else if (r.status === 429) { toast('제출 횟수를 초과했습니다.'); }
          else { toast((r.data && r.data.error && r.data.error.message) || 'RSVP 제출에 실패했습니다.'); }
        })
        .catch(function () { btn.disabled = false; btn.textContent = orig; toast('네트워크 오류가 발생했습니다.'); });
    }, true);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
</script>
`;
//# sourceMappingURL=rsvp.js.map